import { CommonModule } from '@angular/common'
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, forwardRef } from '@angular/core'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'
import { FileService } from '@services'

type TextStyle = 'display' | 'heading-1' | 'heading-2' | 'heading-3' | 'subtitle-1' | 'body' | 'caption' | 'links'
type ImageAlign = 'left' | 'center' | 'right'

const IMAGE_ALIGN_MARGIN: Record<ImageAlign, string> = {
  left: '15px auto 15px 0',
  center: '15px auto',
  right: '15px 0 15px auto',
}

@Component({
  selector: 'app-html-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './html-editor.component.html',
  styleUrls: ['./html-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HtmlEditorComponent),
      multi: true,
    },
  ],
})
export class HtmlEditorComponent implements ControlValueAccessor {
  @Input() placeholder = 'Digite aqui...'
  @Input() simple: Boolean = false

  constructor(private fileService: FileService) {}

  @Output() htmlChange = new EventEmitter<string>()

  @ViewChild('editor', { static: true }) editorRef!: ElementRef<HTMLDivElement>
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>

  disabled = false
  private _value = ''

  readonly textColorOptions: Array<{ label: string; value: string }> = [
    // Default
    { label: 'padrão', value: '' },

    // Neutral
    { label: 'black', value: '#000000' },
    { label: 'white', value: '#FFFFFF' },

    // Common
    { label: 'red', value: '#D21828' },
    { label: 'green', value: '#16A748' },
    { label: 'blue', value: '#165EA7 ' },

    // Electrolux colors

    // primary palette
    { label: 'electrolux blue', value: '#011E41' },

    // secondary palette
    { label: 'moss mid', value: '#6C7B67' },
    { label: 'moss rich', value: '#3B4536' },

    { label: 'umber mid', value: '#957359' },
    { label: 'umber rich', value: '#4A1E18' },

    { label: 'mahogany mid', value: '#7E6464' },
    { label: 'mahogany rich', value: '#3D2727' },

    { label: 'silk mid', value: '#797487' },
    { label: 'silk rich', value: '#2B2936' },
  ]

  showImagePanel = false
  pendingFile: File | null = null

  imageWidth: string | number | null = null
  imageHeight: string | number | null = null
  imageAlt = ''
  imageAlign: ImageAlign = 'left'

  readonly imageAlignOptions: Array<{ label: string; value: ImageAlign }> = [
    { label: 'Esquerda', value: 'left' },
    { label: 'Centro', value: 'center' },
    { label: 'Direita', value: 'right' },
  ]

  editingImage: HTMLImageElement | null = null

  uploadingImage = false
  uploadError = ''

  // Última posição do cursor dentro do editor e posição onde a imagem será inserida
  private lastRange: Range | null = null
  private imageInsertRange: Range | null = null

  private onChange: (v: string) => void = () => {}
  private onTouched: () => void = () => {}

  private readonly emptyWrapperTags = new Set([
    'P',
    'DIV',
    'SPAN',
    'STRONG',
    'EM',
    'U',
    'B',
    'I',
    'A',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'LI',
    'UL',
    'OL',
    'BLOCKQUOTE',
  ])

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      const li = this.getCurrentListItem()
      if (li && !event.shiftKey) {
        if (!this.hasContent(li)) {
          // Enter em item vazio: sai da lista
          event.preventDefault()
          event.stopPropagation()
          const isOrdered = li.parentElement?.tagName.toUpperCase() === 'OL'
          this.exec(isOrdered ? 'insertOrderedList' : 'insertUnorderedList')
        }
        // Enter em item com conteúdo: comportamento nativo cria o próximo item
        return
      }

      event.preventDefault()
      event.stopPropagation()
      this.insertLineBreak()
      this.onInput()
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault()

    const text = event.clipboardData?.getData('text/plain') ?? ''
    if (!text) return

    const html = this.escapeHtml(text).replace(/\r?\n/g, '<br>')
    this.insertHtml(html)
  }

  private insertLineBreak(): void {
    if (this.disabled) return

    this.focusEditor()
    document.execCommand('insertLineBreak')
    this.emit()
  }

  writeValue(value: string | null): void {
    this._value = this.normalizeEditorHtml(value ?? '')
    if (this.editorRef?.nativeElement) {
      this.editorRef.nativeElement.innerHTML = this._value || ''
      this.ensurePlaceholderState()
    }
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
    if (isDisabled) this.closeImagePanel()
  }

  private focusEditor(): void {
    if (this.disabled) return
    this.editorRef.nativeElement.focus()
  }

  private exec(cmd: string, value?: string): void {
    if (this.disabled) return
    this.focusEditor()
    document.execCommand(cmd, false, value)
    this.emit()
  }

  private emit(): void {
    const html = this.editorRef.nativeElement.innerHTML ?? ''
    const normalizedHtml = this.normalizeEditorHtml(html)

    if (!normalizedHtml && html && !this.hasListElement()) {
      this.editorRef.nativeElement.innerHTML = ''
    }

    this._value = normalizedHtml
    this.onChange(normalizedHtml)
    this.htmlChange.emit(normalizedHtml)
    this.ensurePlaceholderState()
  }

  onInput(): void {
    this.emit()
  }

  onBlur(): void {
    this.onTouched()
  }

  @HostListener('document:selectionchange')
  onSelectionChange(): void {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return

    const range = sel.getRangeAt(0)
    if (this.editorRef.nativeElement.contains(range.commonAncestorContainer)) {
      this.lastRange = range.cloneRange()
    }
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled || this.simple) return
    if (event.dataTransfer?.types.includes('Files')) event.preventDefault()
  }

  onDrop(event: DragEvent): void {
    if (this.disabled || this.simple) return

    const file = Array.from(event.dataTransfer?.files ?? []).find((f) => f.type.startsWith('image/'))
    if (!file) return

    // Imagem arrastada do computador: abre o painel já com o arquivo e insere no ponto onde foi solta
    event.preventDefault()
    this.openImagePanel()
    this.pendingFile = file
    this.imageInsertRange = this.getRangeFromPoint(event.clientX, event.clientY)
  }

  private getRangeFromPoint(x: number, y: number): Range | null {
    const doc = document as any

    if (doc.caretPositionFromPoint) {
      const pos = doc.caretPositionFromPoint(x, y)
      if (!pos) return null
      const range = document.createRange()
      range.setStart(pos.offsetNode, pos.offset)
      range.collapse(true)
      return range
    }

    return doc.caretRangeFromPoint?.(x, y) ?? null
  }

  setTextStyle(style: TextStyle): void {
    if (this.disabled) return
    this.focusEditor()

    const block = this.getClosestBlockElement()
    if (!block) {
      this.insertEmptyStyledBlock(style)
      this.emit()
      return
    }

    const el = this.ensureCorrectBlockTag(block, style)
    this.replaceTypographyClass(el, style)

    this.emit()
  }

  setTextColor(colorHex: string | ''): void {
    if (this.disabled) return

    this.focusEditor()

    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return

    const range = sel.getRangeAt(0)
    const common = range.commonAncestorContainer
    const commonEl = (common.nodeType === Node.ELEMENT_NODE ? common : common.parentElement) as HTMLElement | null

    if (!commonEl || !this.editorRef.nativeElement.contains(commonEl)) return

    let frag: DocumentFragment
    try {
      frag = range.extractContents()
    } catch {
      frag = range.cloneRange().cloneContents()
      range.deleteContents()
    }

    const innerSpans = frag.querySelectorAll('span')
    innerSpans.forEach((innerSpan) => {
      while (innerSpan.firstChild) {
        innerSpan.parentNode?.insertBefore(innerSpan.firstChild, innerSpan)
      }
      innerSpan.remove()
    })

    const emptyElements = frag.querySelectorAll('span, strong, em, u, b, i')
    emptyElements.forEach((el) => {
      if (!el.textContent?.trim() && el.children.length === 0) {
        el.remove()
      }
    })

    const span = document.createElement('span')
    span.style.color = colorHex
    span.appendChild(frag)
    range.insertNode(span)

    const nextRange = document.createRange()
    nextRange.setStartAfter(span)
    nextRange.collapse(true)
    sel.removeAllRanges()
    sel.addRange(nextRange)

    this.emit()
  }

  bold(): void {
    this.exec('bold')
  }

  italic(): void {
    this.exec('italic')
  }

  createLink(): void {
    if (this.disabled) return
    this.focusEditor()

    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return

    const url = window.prompt('URL do link (https://...)')
    if (!url) return

    const safeUrl = this.normalizeUrl(url)

    this.trimSelectionRange(sel)
    const range = sel.getRangeAt(0)

    if (sel.isCollapsed) {
      const anchor = document.createElement('a')
      anchor.setAttribute('href', safeUrl)
      anchor.setAttribute('target', '_blank')
      anchor.setAttribute('rel', 'noopener noreferrer')
      anchor.textContent = safeUrl // XSS-safe

      range.insertNode(anchor)
      range.setStartAfter(anchor)
      range.collapse(true)
      sel.removeAllRanges()
      sel.addRange(range)

      this.emit()
      return
    }

    let frag: DocumentFragment
    try {
      frag = range.extractContents()
    } catch {
      frag = range.cloneRange().cloneContents()
      range.deleteContents()
    }

    const existingAnchors = frag.querySelectorAll('a')
    existingAnchors.forEach((anchor) => {
      anchor.replaceWith(...Array.from(anchor.childNodes))
    })

    const walkTextNodes = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim()) {
        const anchor = document.createElement('a')
        anchor.setAttribute('href', safeUrl)
        anchor.setAttribute('target', '_blank')
        anchor.setAttribute('rel', 'noopener noreferrer')

        node.parentNode?.replaceChild(anchor, node)
        anchor.appendChild(node)
      } else {
        Array.from(node.childNodes).forEach(walkTextNodes)
      }
    }

    Array.from(frag.childNodes).forEach(walkTextNodes)

    range.insertNode(frag)
    sel.removeAllRanges()
    sel.addRange(range)
    this.emit()
  }

  private trimSelectionRange(sel: Selection): void {
    if (sel.isCollapsed || sel.rangeCount === 0) return

    const range = sel.getRangeAt(0)
    let text = range.toString()

    const startWhitespaceCount = text.length - text.trimStart().length
    const endWhitespaceCount = text.length - text.trimEnd().length

    if (startWhitespaceCount > 0) {
      try {
        range.setStart(range.startContainer, range.startOffset + startWhitespaceCount)
      } catch {
        // fallback closure protection
      }
    }

    if (endWhitespaceCount > 0) {
      try {
        range.setEnd(range.endContainer, range.endOffset - endWhitespaceCount)
      } catch {
        // fallback closure protection
      }
    }

    sel.removeAllRanges()
    sel.addRange(range)
  }

  unlink(): void {
    if (this.disabled) return
    this.focusEditor()

    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) {
      this.emit()
      return
    }

    const range = sel.getRangeAt(0)

    if (sel.isCollapsed) {
      const startNode = range.startContainer
      const anchor =
        startNode.nodeType === Node.ELEMENT_NODE
          ? (startNode as Element).closest('a')
          : startNode.parentElement?.closest('a')

      if (anchor && anchor.parentNode) {
        const fragment = document.createDocumentFragment()
        while (anchor.firstChild) {
          fragment.appendChild(anchor.firstChild)
        }
        anchor.parentNode.replaceChild(fragment, anchor)
        this.emit()
      }

      return
    }

    let frag: DocumentFragment
    try {
      frag = range.extractContents()
    } catch {
      frag = range.cloneRange().cloneContents()
      range.deleteContents()
    }

    const anchors = frag.querySelectorAll('a')
    anchors.forEach((anchor) => {
      anchor.replaceWith(...Array.from(anchor.childNodes))
    })

    const allElements = frag.querySelectorAll<HTMLElement>('*')
    allElements.forEach((el) => {
      el.removeAttribute('style')
    })

    range.insertNode(frag)
    sel.removeAllRanges()
    sel.addRange(range)
    this.emit()
  }

  bulletList(): void {
    this.toggleList('insertUnorderedList', 'ul')
  }

  orderedList(): void {
    this.toggleList('insertOrderedList', 'ol')
  }

  private toggleList(cmd: 'insertUnorderedList' | 'insertOrderedList', tag: 'ul' | 'ol'): void {
    if (this.disabled) return

    const editor = this.editorRef.nativeElement
    this.focusEditor()

    const sel = window.getSelection()
    const selectionInEditor = !!sel && sel.rangeCount > 0 && editor.contains(sel.getRangeAt(0).commonAncestorContainer)
    if (!selectionInEditor) this.placeCaretInside(editor)

    if (!this.hasContent(editor) && !this.hasListElement()) {
      const list = document.createElement(tag)
      const li = document.createElement('li')
      li.innerHTML = '<br>'
      list.appendChild(li)
      editor.innerHTML = ''
      editor.appendChild(list)
      this.placeCaretInside(li)
      this.emit()
      return
    }

    document.execCommand(cmd, false)
    this.emit()
  }

  private getCurrentListItem(): HTMLLIElement | null {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return null

    let node: Node | null = sel.getRangeAt(0).startContainer
    if (node.nodeType === Node.TEXT_NODE) node = node.parentNode

    const li = (node as HTMLElement | null)?.closest?.('li') as HTMLLIElement | null
    return li && this.editorRef.nativeElement.contains(li) ? li : null
  }

  private hasListElement(): boolean {
    return !!this.editorRef.nativeElement.querySelector('ul, ol, li')
  }

  alignLeft(): void {
    this.exec('justifyLeft')
  }

  alignCenter(): void {
    this.exec('justifyCenter')
  }

  alignRight(): void {
    this.exec('justifyRight')
  }

  insertTable(): void {
    if (this.disabled) return

    const rowsStr = window.prompt('Quantidade de linhas?', '3')
    const colsStr = window.prompt('Quantidade de colunas?', '3')

    const rows = Math.max(1, Number(rowsStr ?? 0) || 3)
    const cols = Math.max(1, Number(colsStr ?? 0) || 3)

    const tableHtml = this.buildTableHtmlInline(rows, cols)
    this.insertHtml(tableHtml)
  }

  undo(): void {
    this.exec('undo')
  }

  redo(): void {
    this.exec('redo')
  }

  clear(): void {
    if (this.disabled) return
    this.editorRef.nativeElement.innerHTML = ''
    this.emit()
  }

  openImagePanel(): void {
    if (this.disabled) return

    this.showImagePanel = true
    this.pendingFile = null
    this.editingImage = null
    this.imageInsertRange = this.lastRange?.cloneRange() ?? null

    this.imageWidth = null
    this.imageHeight = null
    this.imageAlt = ''
    this.imageAlign = 'left'

    this.uploadingImage = false
    this.uploadError = ''

    queueMicrotask(() => {
      if (this.fileInputRef?.nativeElement) this.fileInputRef.nativeElement.value = ''
    })
  }

  closeImagePanel(): void {
    this.showImagePanel = false
    this.pendingFile = null
    this.imageWidth = null
    this.imageHeight = null
    this.imageAlt = ''
    this.imageAlign = 'left'
    this.editingImage = null
    this.imageInsertRange = null
    this.uploadingImage = false
    this.uploadError = ''
  }

  onEditorClick(event: MouseEvent): void {
    if (this.disabled || this.simple) return

    const target = event.target as HTMLElement | null
    if (!(target instanceof HTMLImageElement) || !this.editorRef.nativeElement.contains(target)) return

    this.showImagePanel = true
    this.pendingFile = null
    this.editingImage = target
    this.imageAlt = target.getAttribute('alt') ?? ''
    this.imageWidth = target.style.width || null
    this.imageHeight = target.style.height && target.style.height !== 'auto' ? target.style.height : null
    this.imageAlign = this.getImageAlign(target)
    this.uploadingImage = false
    this.uploadError = ''

    queueMicrotask(() => {
      if (this.fileInputRef?.nativeElement) this.fileInputRef.nativeElement.value = ''
    })
  }

  confirmImagePanel(): Promise<void> {
    return this.editingImage ? this.saveEditedImage() : this.confirmUploadAndInsertImage()
  }

  private async saveEditedImage(): Promise<void> {
    const img = this.editingImage
    if (this.disabled || !img) return

    this.uploadingImage = true
    this.uploadError = ''

    try {
      let url: string | null = null
      if (this.pendingFile) {
        url = await this.uploadImageToBackend(this.pendingFile)
        if (!url) {
          this.uploadError = 'Upload não retornou URL.'
          return
        }
      }

      if (this.editorRef.nativeElement.contains(img)) {
        if (url) img.setAttribute('src', url)
        img.setAttribute('alt', this.imageAlt.trim())
        img.style.width = this.resolveDimension(this.imageWidth, '100%')
        img.style.height = this.resolveDimension(this.imageHeight, 'auto')
        img.style.display = 'block'
        img.style.margin = IMAGE_ALIGN_MARGIN[this.imageAlign]
        this.emit()
      }

      this.closeImagePanel()
    } catch (e: any) {
      this.uploadError = e?.message || 'Falha ao fazer upload da imagem.'
    } finally {
      this.uploadingImage = false
    }
  }

  private getImageAlign(img: HTMLImageElement): ImageAlign {
    const left = img.style.marginLeft === 'auto'
    const right = img.style.marginRight === 'auto'

    if (left && right) return 'center'
    if (left) return 'right'
    return 'left'
  }

  private resolveDimension(value: string | number | null, fallback: string): string {
    if (!value) return fallback

    const n = this.parseDimension(value)
    if (!n) return fallback

    const isPercent = typeof value === 'string' && value.trim().endsWith('%')
    return n + (isPercent ? '%' : 'px')
  }

  onPickFileClick(): void {
    if (this.disabled) return
    this.fileInputRef.nativeElement.click()
  }

  onFileSelected(evt: Event): void {
    const input = evt.target as HTMLInputElement
    this.pendingFile = input.files?.[0] ?? null
    this.uploadError = ''
  }

  private async confirmUploadAndInsertImage(): Promise<void> {
    if (this.disabled) return

    if (!this.pendingFile) {
      this.uploadError = 'Selecione uma imagem do computador.'
      return
    }

    this.uploadingImage = true
    this.uploadError = ''

    try {
      const url = await this.uploadImageToBackend(this.pendingFile)
      if (!url) {
        this.uploadError = 'Upload não retornou URL.'
        return
      }

      const w = this.resolveDimension(this.imageWidth, '100%')
      const h = this.resolveDimension(this.imageHeight, 'auto')

      const baseStyle = [
        'display:block',
        `margin:${IMAGE_ALIGN_MARGIN[this.imageAlign]}`,
        'border-radius:8px',
        'max-width:100%',
      ]

      const styleParts: string[] = [...baseStyle]
      if (w) styleParts.push(`width:${w}`)
      if (h) styleParts.push(`height:${h}`)
      if (!w && !h) styleParts.push('height:auto')

      const style = styleParts.join(';') + ';'

      this.focusEditor()
      this.restoreRange(this.imageInsertRange)
      this.insertHtml(
        `<img src="${this.escapeAttr(url)}" alt="${this.escapeAttr(this.imageAlt.trim())}" style="${style}" />`
      )
      this.closeImagePanel()
    } catch (e: any) {
      this.uploadError = e?.message || 'Falha ao fazer upload da imagem.'
    } finally {
      this.uploadingImage = false
    }
  }

  private async uploadImageToBackend(file: File): Promise<string> {
    const response = await this.fileService.save(file)
    return response.url
  }

  private parseDimension(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined) return null
    const v = String(value).trim().replace(/\D/g, '')
    if (!v) return null

    const n = Number(v)
    if (!Number.isFinite(n) || n <= 0) return null

    return Math.round(n)
  }

  private insertHtml(html: string): void {
    if (this.disabled) return

    this.focusEditor()
    document.execCommand('insertHTML', false, html)
    this.emit()
  }

  private readonly typographyClasses: TextStyle[] = [
    'display',
    'heading-1',
    'heading-2',
    'heading-3',
    'subtitle-1',
    'body',
    'caption',
    'links',
  ]

  private getTagForStyle(style: TextStyle): 'p' | 'h1' | 'h2' | 'h3' {
    if (style === 'heading-1') return 'h1'
    if (style === 'heading-2') return 'h2'
    if (style === 'heading-3') return 'h3'
    return 'p'
  }

  private insertEmptyStyledBlock(style: TextStyle): void {
    const tag = this.getTagForStyle(style)
    const el = document.createElement(tag)
    el.innerHTML = '<br>'
    el.classList.add(style)

    this.editorRef.nativeElement.appendChild(el)
    this.placeCaretInside(el)
  }

  private ensureCorrectBlockTag(block: HTMLElement, style: TextStyle): HTMLElement {
    const expectedTag = this.getTagForStyle(style).toUpperCase()

    if (block === this.editorRef.nativeElement) {
      const el = document.createElement(expectedTag)
      el.innerHTML = '<br>'
      block.appendChild(el)
      this.placeCaretInside(el)
      return el
    }

    if (block.tagName === expectedTag) return block

    const newEl = document.createElement(expectedTag)
    newEl.innerHTML = block.innerHTML

    block.replaceWith(newEl)
    this.placeCaretInside(newEl)
    return newEl
  }

  private replaceTypographyClass(el: HTMLElement, style: TextStyle): void {
    for (const c of this.typographyClasses) el.classList.remove(c)
    el.classList.add(style)
  }

  private getClosestBlockElement(): HTMLElement | null {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return null

    let node: Node | null = sel.getRangeAt(0).commonAncestorContainer
    if (node.nodeType === Node.TEXT_NODE) node = node.parentNode

    let el = node as HTMLElement | null

    while (el && el !== this.editorRef.nativeElement) {
      const tag = el.tagName?.toUpperCase()
      const isBlock =
        tag === 'P' ||
        tag === 'DIV' ||
        tag === 'LI' ||
        tag === 'H1' ||
        tag === 'H2' ||
        tag === 'H3' ||
        tag === 'H4' ||
        tag === 'H5' ||
        tag === 'H6'

      if (isBlock) return el
      el = el.parentElement
    }

    return this.editorRef.nativeElement
  }

  private restoreRange(range: Range | null): void {
    const editor = this.editorRef.nativeElement

    if (!range || !editor.contains(range.startContainer)) {
      this.placeCaretInside(editor)
      return
    }

    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
  }

  private placeCaretInside(el: HTMLElement): void {
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)

    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
  }

  private buildTableHtmlInline(rows: number, cols: number): string {
    const wrapStyle = 'overflow:auto;margin:10px 0;'
    const tableStyle = 'border-collapse:collapse;min-width:420px;'
    const thStyle = 'border:1px solid black;padding:8px 10px;vertical-align:top;background:#f3f3f3;font-weight:600;'
    const tdStyle = 'border:1px solid black;padding:8px 10px;vertical-align:top;'

    const header = `<tr>${Array.from({ length: cols })
      .map(() => `<th style="${thStyle}">Header</th>`)
      .join('')}</tr>`

    const body = Array.from({ length: rows })
      .map(
        () =>
          `<tr>${Array.from({ length: cols })
            .map(() => `<td style="${tdStyle}">&nbsp;</td>`)
            .join('')}</tr>`
      )
      .join('')

    return `
      <div style="${wrapStyle}">
        <table style="${tableStyle}">
          <thead>${header}</thead>
          <tbody>${body}</tbody>
        </table>
      </div>
      <p><br></p>
    `
  }

  private normalizeUrl(url: string): string {
    const trimmed = url.trim()
    if (!trimmed) return trimmed
    if (/^https?:\/\//i.test(trimmed)) return trimmed
    return `https://${trimmed}`
  }

  private ensurePlaceholderState(): void {
    const el = this.editorRef.nativeElement
    const isEmpty = this.normalizeEditorHtml(el.innerHTML ?? '') === '' && !this.hasListElement()
    el.classList.toggle('is-empty', isEmpty)
  }

  private normalizeEditorHtml(value: string): string {
    const html = value.trim()
    if (!html) return ''

    const probe = document.createElement('div')
    probe.innerHTML = value

    return this.hasContent(probe) ? value : ''
  }

  private hasContent(node: Node): boolean {
    if (node.nodeType === Node.TEXT_NODE) {
      return Boolean(node.textContent?.replace(/[\s\u00A0\u200B]/g, ''))
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return false

    const element = node as HTMLElement
    const tagName = element.tagName.toUpperCase()

    if (tagName === 'BR') return false
    if (!this.emptyWrapperTags.has(tagName)) return true

    return Array.from(element.childNodes).some((child) => this.hasContent(child))
  }

  private escapeAttr(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }

  private escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }
}
